import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Helmet } from 'react-helmet';
import { useHistory, useLocation } from 'react-router';

import { Alert, Box, CircularProgress, Container, Link, TextField, Tooltip } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';
import PageSubheader from '~/components/Common/Elements/PageSubheader';

import { useGetMessageArchives } from '~/hooks/apis/customer-communication/useMessageArchives';
import useQuery from '~/hooks/useQuery';

import { formatDateWithClock } from '~/services/TimeService';

import { addQuery } from '~/utils/url';

const SIZE_PER_PAGE = 20;
const SEARCH_DEBOUNCE_MS = 400;

function MarketingEmailsPageContainer() {
  const location = useLocation();
  const query = useQuery();
  const { push: setQueryString } = useHistory();

  const currentPage = Number(query.get('page')) || 1;
  const userId = query.get('userId') ?? '';
  const keyword = query.get('keyword') ?? '';

  const [searchInput, setSearchInput] = useState(keyword);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const locationRef = useRef(location);
  locationRef.current = location;

  useEffect(() => {
    setSearchInput(keyword);
  }, [keyword]);

  const offset = (currentPage - 1) * SIZE_PER_PAGE;

  const { data, isLoading, error } = useGetMessageArchives(userId, offset, SIZE_PER_PAGE, keyword || undefined);

  const rows = data?.result ?? [];
  const totalCount = data?.totalCount ?? 0;

  const columns: Array<GridColDef> = [
    {
      field: 'notificationType',
      headerName: 'Notification Type',
      width: 280,
      editable: false,
      display: 'flex',
      renderCell: (params) => {
        const cell = <span>{params.value}</span>;
        return params.row.description ? (
          <Tooltip title={params.row.description} placement="top">
            {cell}
          </Tooltip>
        ) : (
          cell
        );
      },
    },
    {
      field: 'sentAt',
      headerName: 'Sent',
      width: 160,
      editable: false,
      renderCell: (params) => (params.value ? formatDateWithClock(params.value) : '-'),
      display: 'flex',
    },
    {
      field: 'deliveredAt',
      headerName: 'Delivered',
      width: 160,
      editable: false,
      renderCell: (params) => (params.value ? formatDateWithClock(params.value) : '-'),
      display: 'flex',
    },
    {
      field: 'openedAt',
      headerName: 'Opened',
      width: 160,
      editable: false,
      renderCell: (params) => (params.value ? formatDateWithClock(params.value) : '-'),
      display: 'flex',
    },
    {
      field: 'clickedAt',
      headerName: 'Clicked',
      width: 160,
      editable: false,
      renderCell: (params) => (params.value ? formatDateWithClock(params.value) : '-'),
      display: 'flex',
    },
    {
      field: 'bouncedAt',
      headerName: 'Bounced',
      width: 160,
      editable: false,
      renderCell: (params) => (params.value ? formatDateWithClock(params.value) : '-'),
      display: 'flex',
    },
    {
      field: 'view_email',
      headerName: 'View Email',
      width: 150,
      editable: false,
      renderCell: (params) => (
        <Link
          color="primary"
          target="_blank"
          rel="noreferrer"
          href={`/customer-communication/email/render?id=${params.row.id}`}
          underline="hover"
        >
          View Email
        </Link>
      ),
      display: 'flex',
    },
  ];

  const onPageChange = useCallback(
    (page: number) => {
      setQueryString(addQuery(location, { page, userId }));
    },
    [location, setQueryString, userId],
  );

  const onSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        setQueryString(
          addQuery(locationRef.current, {
            keyword: value || undefined,
            page: 1,
            userId,
          }),
        );
      }, SEARCH_DEBOUNCE_MS);
    },
    [setQueryString, userId],
  );

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>Marketing Emails</title>
      </Helmet>
      <PageSubheader title={`Marketing Emails for User: ${userId}`} />
      <Box mt={2}>
        <TextField
          value={searchInput}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search by notification type, canvas name, or description"
          size="small"
          sx={{ mb: 2, width: 480 }}
        />
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load marketing emails. Please try again.
          </Alert>
        )}
        {isLoading ? (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            loading={isLoading}
            rows={rows}
            columns={columns}
            disableRowSelectionOnClick
            autoHeight
            pagination
            paginationMode="server"
            paginationModel={{
              page: currentPage - 1,
              pageSize: SIZE_PER_PAGE,
            }}
            pageSizeOptions={[SIZE_PER_PAGE]}
            slots={{ pagination: GridPagination }}
            onPaginationModelChange={({ page }) => onPageChange(page + 1)}
            getRowHeight={() => 'auto'}
            rowCount={totalCount}
          />
        )}
      </Box>
    </Container>
  );
}

export default MarketingEmailsPageContainer;
